export type QueueGroup =
  | 'core'
  | 'dossier'
  | 'veeva'
  | 'source-processing'
  | 'core-folder-sync'
  | 'lmr'
  | 'lmr-role-sync'
  | 'slr-fulltext'
  | 'slr-ai-run'
  | 'slr-de'
  | 'slr-rob'
  | 'slr-tos'
  | 'slr-de-pivot'
  | 'slr-abstract-file'
  | 'ocr-stress'
  | string;

/** Normalized status from list/detail endpoints. */
export type JobNormalizedStatus =
  | 'waiting'
  | 'in progress'
  | 'completed'
  | 'failed'
  | 'delayed';

/** BullMQ raw state names used by the `states` query param. */
export type JobBullState =
  | 'waiting'
  | 'active'
  | 'completed'
  | 'failed'
  | 'delayed';

export type JobAction =
  | 'remove'
  | 'retry'
  | 'promote'
  | 'discard'
  | 'cancel'
  | 'force-remove';

export interface QueueJobCounts {
  waiting: number;
  active: number;
  completed: number;
  failed: number;
  delayed: number;
  paused: number;
}

export interface QueueSummary {
  name: string;
  group: QueueGroup;
  counts: QueueJobCounts | null;
  error?: string;
}

export interface QueueGroupSummary {
  group: QueueGroup;
  queueCount: number;
}

export interface QueuesOverviewResponse {
  env: string;
  totalQueues: number;
  groups: QueueGroupSummary[];
  queues: QueueSummary[];
}

export interface QueueJob {
  id: string;
  queue: string;
  group: QueueGroup;
  name: string;
  status: JobNormalizedStatus;
  progress: number | Record<string, unknown>;
  attemptsMade: number;
  failedReason: string;
  createdAt: number | null;
  processedOn: number | null;
  finishedOn: number | null;
  folderId?: string;
  sourceId?: string;
  fileId?: string;
  fileKey?: string;
  producerEnv?: string;
  producerDbName?: string;
  producedAt?: string;
  consumerEnv?: string;
  consumerDbName?: string;
}

export interface JobsListResponse {
  total: number;
  returned: number;
  offset: number;
  limit: number;
  hasMore: boolean;
  queuesScanned: string[];
  jobs: QueueJob[];
}

export interface JobDetail extends QueueJob {
  data?: unknown;
  producerMongoFingerprint?: string;
  consumerMongoFingerprint?: string;
  mongoFingerprintMismatch?: boolean;
}

export interface JobLogsResponse {
  jobId: string;
  queue: string;
  group: QueueGroup;
  logs: string[];
  count: number;
}

export interface JobActionResult {
  jobId: string;
  queue: string;
  name: string;
  action: JobAction;
  ok: boolean;
  reason?: string;
  details?: { active?: boolean; wasActive?: boolean };
}

export interface JobActionResponse extends JobActionResult {
  message: string;
  group: QueueGroup;
}

export interface BulkJobActionResponse {
  message: string;
  action: JobAction;
  attempted: number;
  succeeded: number;
  failedCount: number;
  results: JobActionResult[];
}

/** Sortable columns for GET /api/status/jobs. */
export type JobSortBy =
  | 'id'
  | 'name'
  | 'queue'
  | 'group'
  | 'status'
  | 'progress'
  | 'attempts'
  | 'createdAt';

export type JobSortDir = 'asc' | 'desc';

export interface JobsListParams {
  queue?: string;
  group?: string;
  status?: JobNormalizedStatus;
  name?: string;
  states?: string;
  limit?: number;
  offset?: number;
  sortBy?: JobSortBy;
  sortDir?: JobSortDir;
}

export interface SingleJobActionBody {
  action: JobAction;
  queue: string;
}

export interface BulkJobActionBody {
  action: JobAction;
  jobIds?: Array<{ id: string; queue: string }>;
  queue?: string;
  group?: string;
  status?: JobNormalizedStatus;
  states?: string[] | string;
  limit?: number;
}

export const QUEUE_GROUPS: QueueGroup[] = [
  'core',
  'dossier',
  'veeva',
  'source-processing',
  'core-folder-sync',
  'lmr',
  'lmr-role-sync',
  'slr-fulltext',
  'slr-ai-run',
  'slr-de',
  'slr-rob',
  'slr-tos',
  'slr-de-pivot',
  'slr-abstract-file',
  'ocr-stress',
];

export const JOB_STATUSES: JobNormalizedStatus[] = [
  'waiting',
  'in progress',
  'completed',
  'failed',
  'delayed',
];
